import {ROUTES} from '../../app/routes';
import {Link, useRouter} from '../../app/router';
import {useCart} from '../../cart';
import {BCP47, useI18n, type Language, type MessageKey} from '../../i18n';
import {Button, OptionGroup, OptionRow, Screen, ScreenBody, ScreenFooter} from '../../ui';
import './styles.css';

/**
 * # Q2 · Language toggle
 *
 * Route `/language`, **and** the inline control every other screen embeds.
 *
 * ## What this screen is for
 *
 * A tourist whose phone reported `en-GB` at a venue whose default is Thai, or a
 * Thai guest whose phone reported `en-US` because it was bought abroad. Either
 * one is currently looking at a menu they cannot read, and this is the one
 * control that fixes it. It has to be findable from wherever they gave up.
 *
 * ## Rules this file holds to
 *
 * * **Own script, never flags.** `ไทย` / `English` / `中文`, identical in all
 *   three dictionaries on purpose (`q2.th` / `q2.en` / `q2.zh`). A flag names a
 *   country and a language is not a country — 中文 is not a flag any more than
 *   English is the Union Jack, and picking one insults somebody.
 * * **No photos, no imagery of any kind.** 03 says design the no-photo state as
 *   the default; on this screen that is not a fallback branch, it is the whole
 *   design. The one glyph left is a globe, inline SVG drawn in `currentColor`,
 *   decorative and `aria-hidden`.
 * * **The cart survives, and so does everything else.** `setLanguage` writes one
 *   string and re-renders. It does not navigate, does not remount a provider,
 *   does not clear storage. The cart holds `LocalizedText` snapshots rather than
 *   rendered strings, so all three languages read the same cart. `q2.cartKept`
 *   says so out loud, but only when there is a cart to be nervous about.
 * * **Nothing here is Pandan.** Selection is Chili Red, the brand accent. Pandan
 *   is server-verified money and only that (D-021); `--color-success` is equally
 *   wrong, because nothing on this screen succeeded.
 * * **No money and no menu items are rendered here**, so the 86'd-mid-session
 *   case has nothing to show. It is still handled — indirectly and correctly —
 *   by the cart holding ids rather than text: an item that vanishes while the
 *   guest is on this screen is flagged by `useCart().issues` the moment they are
 *   back on Q3 or Q5, in whichever language they just chose.
 *
 * ## REDESIGN · the route is now the package's own single-select
 *
 * The picker used to be three `aria-pressed` buttons in bespoke cards, and the
 * comment above them argued against radio semantics: a `radiogroup` promises
 * arrow-key roving focus, and promising it without implementing it is worse than
 * three plainly-labelled buttons.
 *
 * That objection is answered rather than overruled. `OptionRow` is a **real
 * `<input type="radio">` inside a 48px `<label>`**, so the roving focus, the
 * grouping and the announcement are the browser's and not a promise this file
 * makes. It is also the exact control the design package draws for a required
 * single choice — Chili Red ring and dot, page-ground rows, one warm hairline
 * between them — which is what a language chooser is. The bespoke card row and
 * its hand-drawn check mark are both gone; nothing is left here that the shared
 * vocabulary did not already have a word for.
 *
 * ## Two ways to embed it
 *
 * ```tsx
 * <LanguageToggle />                  // segmented: three options, one tap
 * <LanguageToggle variant="link" />   // one 48px pill that opens /language
 * ```
 *
 * The segmented control is the right one wherever the header has room, because a
 * guest who cannot read the page should not have to read a navigation label to
 * escape it. Use `variant="link"` when the header is already carrying a venue
 * name and a cart button — it is the reason the `/language` route exists at all.
 */

/** The label key for each language, in that language's own script. */
const LABEL_KEY: Record<Language, MessageKey> = {
  th: 'q2.th',
  en: 'q2.en',
  zh: 'q2.zh',
};

/** Chinese gets a face hint; the other two are already on the body stack. */
function scriptClass(language: Language): string | undefined {
  return language === 'zh' ? 'q2-script-zh' : undefined;
}

export default function Q02LanguageToggle() {
  const {t, language, setLanguage, languages} = useI18n();
  const {count} = useCart();
  const {navigate, back} = useRouter();

  /**
   * The way out.
   *
   * Back when there is somewhere to go back to, and the menu otherwise. A guest
   * who opened `/language` from a screen returns to that screen; one who landed
   * here directly — a shared link, a restored tab — would otherwise be handed a
   * dead end, and `replace` keeps `/language` out of their history so the next
   * back gesture does not bounce them straight back into the picker.
   */
  const exit = () => {
    if ((globalThis.history?.length ?? 0) > 1) back();
    else navigate(ROUTES.menu, {replace: true});
  };

  return (
    <Screen className="q2-screen">
      <ScreenBody className="q2-body">
        <h1 id="q2-title" className="pk-h1">
          {t('q2.title')}
        </h1>

        {/*
          A `section` labelled by the heading that is already on the page — not a
          second, screen-reader-only copy of the same word. The radio group is
          then announced as "Language" rather than as three loose controls, and
          the `name` is what makes the three one group; the browser does the rest.
        */}
        <section aria-labelledby="q2-title" className="q2-choices">
          <OptionGroup>
            {languages.map((code) => (
              <OptionRow
                key={code}
                type="radio"
                name="q2-language"
                value={code}
                checked={code === language}
                onChange={() => setLanguage(code)}
                label={
                  <span lang={BCP47[code]} className={scriptClass(code)}>
                    {t(LABEL_KEY[code])}
                  </span>
                }
              />
            ))}
          </OptionGroup>
        </section>

        {/*
          Only shown when the guest has something to lose. An empty cart being
          "kept" is a sentence that makes a guest wonder what could have gone
          wrong, which is the opposite of what the line is for.
        */}
        {count > 0 ? <p className="pk-muted">{t('q2.cartKept')}</p> : null}
      </ScreenBody>

      {/*
        The one solid action on the screen, and the only way out on a phone that
        has no system back gesture on a page opened from a QR. No hairline above
        it: the body is short and a rule under three rows reads as a cut-off
        page (the design draws the bordered and the plain footer, and means both).
      */}
      <ScreenFooter bordered={false}>
        <Button variant="primary" onClick={exit}>
          {t('common.done')}
        </Button>
      </ScreenFooter>
    </Screen>
  );
}

/**
 * The inline control. Imported by Q1, Q3, Q5, Q12, Q17, Q19 and Q20.
 *
 * `variant="link"` renders a real `<a href="/language">` and therefore needs the
 * router in context, which every screen has. `variant="segmented"` needs only
 * i18n, so it is safe anywhere — including a header rendered above the router.
 */
export function LanguageToggle({
  variant = 'segmented',
  className,
  onChange,
}: {
  variant?: 'segmented' | 'link';
  /** Merged after the base class, so a host screen can place it. */
  className?: string;
  /** Fired after the switch. For a host that needs to close its own sheet. */
  onChange?: (language: Language) => void;
}) {
  const {t, language, setLanguage, languages} = useI18n();

  if (variant === 'link') {
    return (
      <Link
        to={ROUTES.language}
        className={['q2-link', 'pokpok-tap', className].filter(Boolean).join(' ')}
        aria-label={t('common.changeLanguage')}
      >
        <Globe />
        <span lang={BCP47[language]} className={scriptClass(language)}>
          {t(LABEL_KEY[language])}
        </span>
      </Link>
    );
  }

  return (
    // A group of toggles rather than a radiogroup: this one is embedded in
    // another screen's header, where a radio group would capture arrow keys from
    // whatever else that screen puts beside it. The route above is the full
    // control and is a real radio group; this is a shortcut, and three plainly
    // labelled `aria-pressed` buttons are what a shortcut should be.
    <div
      role="group"
      aria-label={t('common.language')}
      className={['q2-seg', className].filter(Boolean).join(' ')}
    >
      {languages.map((code) => (
        <button
          key={code}
          type="button"
          className={['q2-seg-option', scriptClass(code)].filter(Boolean).join(' ')}
          aria-pressed={code === language}
          lang={BCP47[code]}
          onClick={() => {
            setLanguage(code);
            onChange?.(code);
          }}
        >
          {t(LABEL_KEY[code])}
        </button>
      ))}
    </div>
  );
}

/**
 * The one conventional non-flag mark for "language". Drawn rather than imported
 * so it survives the token-only strip and costs no request.
 */
function Globe() {
  return (
    <svg
      className="q2-globe"
      width="20"
      height="20"
      viewBox="0 0 20 20"
      fill="none"
      aria-hidden="true"
      focusable="false"
    >
      <circle cx="10" cy="10" r="7.5" stroke="currentColor" strokeWidth="1.5" />
      <path
        d="M2.5 10h15M10 2.5c2 2.2 3 4.7 3 7.5s-1 5.3-3 7.5c-2-2.2-3-4.7-3-7.5s1-5.3 3-7.5Z"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinejoin="round"
      />
    </svg>
  );
}
